import React, { useState } from "react";
import { siteInfo } from "../mock";
import { adminLogin } from "../api";
import { AdminAbout } from "./AdminAbout";
import { AdminBlogManager } from "./AdminBlogManager";
import { AdminJoinInfo } from "./AdminJoinInfo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Lock,
  LogOut,
  Users,
  Megaphone,
  Newspaper,
  UserPlus,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password);
      onLogin();
      toast.success("Connexion réussie");
    } catch (err) {
      setError("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <img
              src={siteInfo.logo}
              alt="UNSA"
              className="h-16 w-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-slate-800">Administration</h1>
            <p className="text-sm text-slate-500 mt-1">UNSA AEGIDE DOMITYS</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  className="pl-10"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 font-medium">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-2.5 bg-sky-500 text-white font-semibold rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={siteInfo.logo} alt="UNSA" className="h-10 w-10" />
              <div>
                <span className="font-bold text-slate-800">Administration</span>
                <Badge className="ml-2 bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100 text-xs">
                  Admin
                </Badge>
              </div>
            </div>
            <button
              onClick={() => setIsLoggedIn(false)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-500 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Admin Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 mb-1">
            Gestion du site
          </h1>
          <p className="text-slate-500 text-sm">
            Modifiez le contenu de votre site UNSA AEGIDE DOMITYS
          </p>
        </div>

        <Tabs defaultValue="about" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap gap-1">
            <TabsTrigger value="about" className="flex items-center gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <Users className="w-4 h-4" />
              Nous connaître
            </TabsTrigger>
            <TabsTrigger value="actions" className="flex items-center gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <Megaphone className="w-4 h-4" />
              Nos actions
            </TabsTrigger>
            <TabsTrigger value="news" className="flex items-center gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <Newspaper className="w-4 h-4" />
              Notre actualité
            </TabsTrigger>
            <TabsTrigger value="join" className="flex items-center gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <UserPlus className="w-4 h-4" />
              Nous rejoindre
            </TabsTrigger>
          </TabsList>

          <TabsContent value="about">
            <AdminAbout />
          </TabsContent>

          <TabsContent value="actions">
            <AdminBlogManager type="actions" label="Actions" />
          </TabsContent>

          <TabsContent value="news">
            <AdminBlogManager type="news" label="Actualités" />
          </TabsContent>

          <TabsContent value="join">
            <AdminJoinInfo />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
